import { useLocation, useNavigate } from "react-router-dom";
import {
  ErrorTxt,
  LoginContainer,
  LoginContainerWrapper,
  LoginMainWrap,
} from "../../style/auth/login";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import Header from "../../components/layout/header";
import { useForm } from "react-hook-form";
import SwitchButton from "../../components/common/switch.button";
import {
  fetchLoadAdminLogin,
  fetchLoadClientLogin,
  LoginProps,
} from "../../api/auth";
import { useAuthStore } from "../../atoms/user.auth.store";
import ModalCenterLayout from "../../components/modal/modal.center.layout";
import AlertModal from "./modal/alert.modal";

interface LoginFormData {
  companyCode: string;
  id: string;
  password: string;
}

interface ModalProps {
  isOpen: boolean;
  isVisible: boolean;
}

const Login = () => {
  const location = useLocation(); // 현재 경로 가져오기
  const [role, setRole] = useState<"user" | "admin">("user");
  const navigate = useNavigate();
  const { login } = useAuthStore(); // ✅ 추가
  const [saveChecked, setSaveChecked] = useState(false);
  const [savedCompanyCode, setSavedCompanyCode] = useState("");
  const [savedId, setSavedId] = useState("");
  const [infoModal, setInfoModal] = useState<ModalProps>({
    isOpen: false,
    isVisible: false,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<LoginFormData>({
    defaultValues: {
      companyCode: savedCompanyCode,
      id: savedId,
      password: "",
    },
  });

  useEffect(() => {
    // 페이지 이동 시 스크롤을 맨 위로 이동
    window.scrollTo(0, 0);
  }, [location.pathname]); // 경로 변경될 때 실행

  // 로컬 스토리지에 jwt 정보가 있으면 로그인 화면으로 이동 금지
  useEffect(() => {
    const jwtToken = localStorage.getItem("jwtToken");
    const role = localStorage.getItem("role");
    const userId = localStorage.getItem("userId");

    if (jwtToken && role) {
      if (role === "user") {
        navigate(`/backup/device/${userId}`);
      } else if (role === "admin") {
        navigate("/backup/user");
      }
    }
  }, [navigate]);

  useEffect(() => {
    const saved = localStorage.getItem("savedLoginInfo");
    if (saved) {
      const parsed = JSON.parse(saved);
      setSavedCompanyCode(parsed.companyCode || "");
      setSavedId(parsed.id || "");
      setRole(parsed.role || "user"); // ✅ 저장된 role 반영
      setSaveChecked(true);

      setValue("companyCode", parsed.companyCode || "");
      setValue("id", parsed.id || "");
    }
  }, []);

  const onSubmit = async (data: LoginFormData) => {
    if (saveChecked) {
      localStorage.setItem(
        "savedLoginInfo",
        JSON.stringify({
          companyCode: data.companyCode,
          id: data.id,
          role,
        })
      );
    } else {
      localStorage.removeItem("savedLoginInfo");
    }

    // 역할에 따라 API 호출 분기
    try {
      let response;

      if (role === "user") {
        const requestData: LoginProps = {
          companyCode: data.companyCode,
          userAccount: data.id,
          password: data.password,
          deviceId: "", // ✅ 사용자용 deviceId 필수 (빈 문자열)
        };

        response = await fetchLoadClientLogin(requestData);

        const { jwtToken, userId, userEmail, userName, companyName } = response;

        console.log(response);

        localStorage.setItem("jwtToken", jwtToken);
        localStorage.setItem("userId", userId);
        localStorage.setItem("role", "user");
        localStorage.setItem("companyCode", data.companyCode);
        localStorage.setItem(
          "userInfo",
          JSON.stringify({
            userAccount: data.id,
            userName,
            userEmail,
            companyName,
          })
        );

        login(userId, "user", jwtToken); // ✅ 상태 저장

        navigate(`/backup/device/${userId}`);
      } else if (role === "admin") {
        const requestData: LoginProps = {
          companyCode: data.companyCode,
          userAccount: data.id,
          password: data.password,
          // ❌ 관리자용은 deviceId 미포함
        };

        // 타입스크립트에서 deviceId가 없어도 되도록 명시적으로 제외
        const { deviceId, ...adminData } = requestData;
        response = await fetchLoadAdminLogin(adminData);

        const { jwtToken, userId, userEmail, userName, companyName } = response;

        console.log(response);

        localStorage.setItem("jwtToken", jwtToken);
        localStorage.setItem("userId", userId);
        localStorage.setItem("role", "admin");
        localStorage.setItem("companyCode", data.companyCode);
        localStorage.setItem(
          "userInfo",
          JSON.stringify({
            userAccount: data.id,
            userName,
            userEmail,
            companyName,
          })
        );

        login(userId, "admin", jwtToken); // ✅ 상태 저장
        navigate("/backup/user");
      }

      // 로그인 성공 후 처리 (예: 토큰 저장, 페이지 이동 등)
    } catch (err: any) {
      console.error("❌ 로그인 실패:", err);
      if (err?.response?.status === 400) {
        setInfoModal({ isOpen: true, isVisible: true });
      } else {
        alert("로그인 중 알 수 없는 오류가 발생했습니다.");
      }
    }
  };

  const infoModalClosed = () => {
    setInfoModal({ isOpen: false, isVisible: false });
  };

  return (
    <LoginContainer>
      <Header />
      <AnimatePresence mode="wait" initial={true}>
        <LoginContainerWrapper>
          <motion.div
            key={location.pathname} // 경로가 변경될 때마다 애니메이션 트리거
            initial={{ opacity: 0, y: 20 }} // 초기 상태 (살짝 오른쪽에서 시작)
            animate={{ opacity: 1, y: 0 }} // 나타날 때
            exit={{ opacity: 0, y: -20 }} // 사라질 때 (왼쪽으로 이동하며 사라짐)
            transition={{ duration: 0.3 }} // 애니메이션 속도
          >
            <LoginMainWrap>
              <div className="inner">
                <div className="login-form">
                  <h3>
                    스피드 백업에 <br /> 오신 것을 환영합니다.
                  </h3>
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="input-wrap">
                      <div className="input-box">
                        <label htmlFor="companyCode">기업코드</label>
                        <input
                          type="text"
                          id="companyCode"
                          defaultValue={savedCompanyCode}
                          {...register("companyCode", {
                            required: "기업코드를 입력해 주세요.",
                          })}
                        />
                        <span></span>
                        {errors.companyCode && (
                          <ErrorTxt>
                            {typeof errors.companyCode.message === "string"
                              ? errors.companyCode.message
                              : ""}
                          </ErrorTxt>
                        )}
                      </div>
                      <div className="input-box">
                        <input
                          type="text"
                          defaultValue={savedId}
                          {...register("id", {
                            required: "아이디를 입력해 주세요.",
                          })}
                        />
                        <span></span>
                        <label htmlFor="companyCode">아이디</label>
                        {errors.id && (
                          <ErrorTxt>
                            {typeof errors.id.message === "string"
                              ? errors.id.message
                              : ""}
                          </ErrorTxt>
                        )}
                      </div>
                      <div className="input-box">
                        <label htmlFor="companyCode">비밀번호</label>
                        <input
                          type="password"
                          {...register("password", {
                            required: "비밀번호를 입력해 주세요.",
                          })}
                        />
                        <span></span>
                        {errors.password && (
                          <ErrorTxt>
                            {typeof errors.password.message === "string"
                              ? errors.password.message
                              : ""}
                          </ErrorTxt>
                        )}
                      </div>
                    </div>
                    <div className="sub-fn">
                      <SwitchButton value={role} onChange={setRole} />
                      <div className="save-fn">
                        <input
                          type="checkbox"
                          id="saveId"
                          checked={saveChecked}
                          onChange={(e) => setSaveChecked(e.target.checked)}
                        />
                        <label htmlFor="saveId">기업코드, 아이디 저장</label>
                      </div>
                    </div>
                    <div className="submit-btn-box">
                      <input type="submit" value="로그인" />
                      <span
                        onClick={() => {
                          navigate(`/join`);
                        }}
                      >
                        서비스 신청
                      </span>
                    </div>
                  </form>
                </div>
                <div className="bg">
                  <img src="/assets/login_bg.png" alt="" />
                </div>
              </div>
            </LoginMainWrap>
          </motion.div>
        </LoginContainerWrapper>
      </AnimatePresence>

      {infoModal.isOpen && (
        <ModalCenterLayout
          isVisible={infoModal.isVisible}
          closedHandler={infoModalClosed}
          size="s"
        >
          <AlertModal closedHandler={infoModalClosed} />
        </ModalCenterLayout>
      )}
    </LoginContainer>
  );
};

export default Login;
